// PostHog, loaded lazily. posthog-js is ~70 KB gzipped; importing it statically put it in the
// main bundle every page downloads before it can render. plugins/posthog.client.ts now loads it
// with a dynamic import once the page is interactive. Components call capture(), which queues
// events until PostHog is ready, and is a no-op when analytics is off (dev, or no key).

import type { PostHog, Properties } from 'posthog-js';

type TState = 'off' | 'loading' | 'ready';

let state: TState = 'off';
let client: PostHog | null = null;
const queue: Array<(posthog: PostHog) => void> = [];

/** Run fn with the PostHog client now, or once it has loaded. Dropped when analytics is off. */
export function withPosthog(fn: (posthog: PostHog) => void) {
    if (state === 'ready' && client) {
        fn(client);
    } else if (state === 'loading') {
        queue.push(fn);
    }
}

export function capture(event: string, properties?: Properties) {
    withPosthog((posthog) => posthog.capture(event, properties));
}

/** The client if it has loaded, else null. */
export function getPosthog(): PostHog | null {
    return client;
}

/** Called by the plugin before components mount, so early capture() calls are queued, not dropped. */
export function markPosthogLoading() {
    state = 'loading';
}

export async function loadPosthog(key: string) {
    try {
        const { default: posthog } = await import('posthog-js');
        posthog.init(key, { api_host: 'https://app.posthog.com', capture_pageleave: false });
        client = posthog;
        state = 'ready';
        queue.splice(0).forEach((fn) => fn(posthog));
    } catch {
        // Blocked by an ad blocker or offline: analytics stays off, the page is unaffected.
        state = 'off';
        queue.length = 0;
    }
}
